import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { useAddressStore } from '@features/shipping-methods'

import { ProductDetail } from '@widgets/product-detail'
import { ProductsSimilar } from '@widgets/products-similar'



import { SEO } from '@shared/components/SEO'
import { BRAND_NAME } from '@shared/contants/app'
import { Product, useProductQuery } from '@shared/generated'
import { useAnalytics } from '@shared/lib/analytics'
import { createProductJsonLd } from '@shared/lib/seo/seo-utils'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import Wrapper from '@shared/ui/wrapper'


export default function ProductDetailPage () {
  const { t } = useTranslation('seo')
  const { slug } = useParams()
  const { shopId } = useAddressStore()
  const { viewItem } = useAnalytics()
  const lastTrackedSlugRef = useRef<string | null>(null)
  const { data } = useProductQuery({
    variables: {
      shopId: shopId,
      slug,
      style: '600xauto',
      previewStyle2: '600xauto',
      fullPreviewStyle: '1920xauto',
      galleryImagesStyle: '1920xauto',
    },
  })

  // Track view_item event when product is loaded (only once per product)
  useEffect(() => {
    if (data?.product && slug && lastTrackedSlugRef.current !== slug) {
      lastTrackedSlugRef.current = slug
      viewItem(data.product as Product)
    }
  }, [ data?.product, slug, viewItem ])

  const jsonLd = useMemo(() => {
    if (!data?.product) return []
    return [
      { id: 'product-jsonld', data: createProductJsonLd(data.product as Product) }
    ]
  }, [ data?.product ])

  return (
    <div className="product-page">
      {data?.product && (
        <SEO
          title={t('product.title', { name: data.product.name })}
          description={data.product.short_description || t('product.description', { name: data.product.name, brand: BRAND_NAME })}
          keywords={`${data.product.name}, ${data.product.brand?.name || ''}, продукты, акции`}
          ogTitle={data.product.name}
          ogDescription={data.product.short_description || undefined}
          ogImage={data.product.preview?.url || data.product.fullPreview?.url || undefined}
          canonicalUrl={`/product/${slug}`}
          jsonLd={jsonLd}
        />
      )}
      <Container>
        <PageGrid>
          <Wrapper
            style={{
              overflow: 'initial',
              position: 'relative',
            }}
          >
            {data && data.product && (
              <>
                <ProductDetail product={data.product as Product} />
                <ProductsSimilar productId={data.product.id} />
              </>
            )}
          </Wrapper>
        </PageGrid>
      </Container>
    </div>
  )
}